import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDocumentSelfieDetection} from '../../../../hooks';
import {IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE, getFeatureFlags} from '@chekin/core';
import {useSummary} from '../../../../context/summary';
import backSidePlaceholder from '@chekin/shared/images/backside-placeholder.svg?no-inline';
import codeSidePlaceholder from '@chekin/shared/images/code-side-placeholder.svg?no-inline';
import {useDiscardLastPhotoBack} from 'hooks/iv/useDiscardLastPhotoBack';
import {isUnattendedTerminal} from 'hooks/terminalMode';
import {
  getDocumentNationality,
  getDocumentType,
  useIVStore,
  useCaptureModeStore,
  CameraCapture,
  type CameraCaptureControls,
} from '@chekin/shared';
import {
  DocumentCameraOutline,
  DocumentPrepGuide,
  getSideVisual,
  useAutofillDocumentVisual,
} from '@chekin/shared/guest-form';
import {cn} from '@chekinapp/ui';
import {PageLayout} from 'components/PageLayout';
import {SkipForNow} from 'components/common/SkipForNow';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {CaptureHints} from '../../components/CaptureHints';
import {
  getIVCaptureChecks,
  showsCodeSide,
  useIVCaptureScreenText,
  useIVCaptureKicker,
} from '../../ivCaptureCopy';

function BackScanView() {
  const {onBack: openDiscardModal, modal: discardModal} = useDiscardLastPhotoBack();
  const {reservationId, countryCode} = useSummary();
  const {t} = useTranslation();
  const {t: tIV} = useTranslation('identity-verification');
  const docType = IV_DOCUMENT_IMAGE_TYPES.back;

  const documentType = useIVStore(getDocumentType);
  const documentNationality = useIVStore(getDocumentNationality);
  const captureMode = useCaptureModeStore(state => state.mode);
  const captureRef = React.useRef<CameraCaptureControls>(null);
  const [autoCaptureUnavailable, setAutoCaptureUnavailable] = React.useState(false);
  const [isIntroVisible, setIsIntroVisible] = React.useState(false);
  const visual = getSideVisual(useAutofillDocumentVisual(), 'back');
  const isGuided = Boolean(visual) && !isUnattendedTerminal();

  const {
    isProcessing,
    processState,
    passedChecks,
    errorMessage,
    errorDescription,
    errorReason,
    failureCount,
    isServiceFailure,
    onTryAgain,
    onVerifyLater,
    canVerifyLater,
    isFinalStep,
    biomatch,
    sendBackSideDocument,
  } = useDocumentSelfieDetection({docType});

  const handleBack = () => {
    if (!isProcessing && captureRef.current?.showIntro()) return;
    openDiscardModal();
  };

  const {copy, title, intro, isChecking} = useIVCaptureScreenText(
    'back',
    processState,
    isProcessing,
    {
      autoCaptureUnavailable,
      documentType,
      presentation: 'ring',
      isIntro: isIntroVisible,
      isFinalStep,
      biomatch,
      errorReason,
      failureCount,
    },
  );
  const isCodeSide = showsCodeSide('back', documentType);

  const kicker = useIVCaptureKicker(copy.kicker);
  const fillsPage = Boolean(isProcessing);
  const fitsPrep = isGuided && isIntroVisible;
  const showsSkip = !isChecking && captureMode !== 'crop';

  const handleChangePhoto = React.useCallback(
    ({image, source}: {image: string; source: IV_PHOTO_SOURCE}) => {
      sendBackSideDocument({
        image,
        source,
        countryCode,
        reservationId,
        documentType: documentType?.value,
        nationality: documentNationality,
      });
    },
    [
      countryCode,
      documentType?.value,
      reservationId,
      sendBackSideDocument,
      documentNationality,
    ],
  );

  return (
    <PageLayout
      kicker={kicker}
      className="iv-back-view"
      fillViewport={fillsPage}
      fitViewport={fitsPrep}
      title={title}
      intro={intro}
      align={isChecking ? 'center' : undefined}
      backLabel={t('back')}
      onBack={handleBack}
      titleAction={showsSkip ? <SkipForNow /> : undefined}
      mobileTitle={t('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
    >
      <div
        className={cn(
          'flex flex-col gap-8',
          fillsPage && 'flex-1',
          fitsPrep && 'lg:min-h-0 lg:flex-1',
        )}
      >
        <CameraCapture
          className={cn('ph-no-capture', fitsPrep && 'lg:min-h-0 lg:flex-1')}
          variant="document"
          controlRef={captureRef}
          allowUpload={!isUnattendedTerminal()}
          allowDeviceCamera={!isUnattendedTerminal()}
          autoCapture={getFeatureFlags('IV_DOCUMENT_AUTO_CAPTURE')}
          intro={
            isGuided && visual ? (
              <DocumentPrepGuide size="wide" fitHeight visual={visual} />
            ) : undefined
          }
          onIntroVisibilityChange={setIsIntroVisible}
          introLabel={tIV('im_ready_open_camera')}
          uploadStyle="button"
          checking={{checks: getIVCaptureChecks('back'), passedChecks, isFinalStep}}
          frameGuide={visual ? <DocumentCameraOutline visual={visual} /> : undefined}
          placeholderImage={
            visual ? undefined : isCodeSide ? codeSidePlaceholder : backSidePlaceholder
          }
          shutterLabel={tIV('take_photo')}
          onCapture={handleChangePhoto}
          onAutoCaptureUnavailable={() => setAutoCaptureUnavailable(true)}
          processState={isProcessing ? processState : undefined}
          statusLabel={visual ? tIV('align_document_with_guide') : copy.statusLabel}
          scanningLabel={copy.scanningLabel}
          successLabel={copy.successLabel}
          errorTitle={errorMessage || copy.errorTitle}
          errorDescription={errorDescription || copy.errorDescription}
          hasKnownCause={Boolean(errorReason)}
          canResend={isServiceFailure}
          hints={
            isIntroVisible ? undefined : (
              <CaptureHints
                variant={isCodeSide ? 'codeSide' : 'document'}
                className="lg:hidden"
              />
            )
          }
          onRetry={onTryAgain}
          onVerifyLater={canVerifyLater ? onVerifyLater : undefined}
        />
      </div>
      {discardModal}
    </PageLayout>
  );
}

export {BackScanView};
